/**
 * HomepageVersionGate — TREATX-LANDING-V3-PHASE-A
 * ------------------------------------------------
 * Resolves the PUBLIC root route (`/`) to the version published server-side
 * through the canonical configuration authority
 * (`GET /api/public/homepage-version`, backed by `system_config.homepage_version`).
 *
 * Invariants:
 *   • V1 is the default and the fail-safe: an error, a timeout, an unknown
 *     label or a missing configuration all render V1.
 *   • Publication state is NEVER read from or written to browser storage.
 *   • This component only chooses a landing component. It changes no route,
 *     API, permission or journey anywhere else in the product.
 */
import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import LandingPage from './LandingPage';
import LandingPageV2 from './landing-v2/LandingPageV2';
import LandingPageV3 from './landing-v3/LandingPageV3';

const VERSION_COMPONENTS = {
  V1: LandingPage,
  V2: LandingPageV2,
  V3: LandingPageV3,
};

const FALLBACK_VERSION = 'V1';
// Deadline for resolving the published version. Past this the visitor gets the
// safe default rather than a spinner; a later response is ignored.
const RESOLVE_TIMEOUT_MS = 2500;

const HomepageVersionGate = () => {
  const [version, setVersion] = useState(null);

  useEffect(() => {
    // A4 — bounded resolution. The homepage must never sit in an indefinite
    // loading state, and a response that arrives after the deadline must not
    // replace the V1 fallback the visitor is already reading.
    let settled = false;
    const controller = new AbortController();

    const settle = (value) => {
      if (settled) return;
      settled = true;
      setVersion(VERSION_COMPONENTS[value] ? value : FALLBACK_VERSION);
    };

    const deadline = setTimeout(() => {
      if (!settled) {
        controller.abort();           // stop the in-flight request
        settle(FALLBACK_VERSION);     // and show the safe default
      }
    }, RESOLVE_TIMEOUT_MS);

    api.get('/public/homepage-version', { signal: controller.signal })
      .then(({ data }) => settle(data?.version))
      .catch(() => settle(FALLBACK_VERSION))
      .finally(() => clearTimeout(deadline));

    return () => {
      // unmount: cancel the request, drop the timer, never touch state again
      settled = true;
      clearTimeout(deadline);
      controller.abort();
    };
  }, []);

  if (!version) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white"
        data-testid="homepage-version-loading"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-slate-700 animate-spin" />
          <span className="sr-only">Loading TreatX</span>
        </div>
      </div>
    );
  }

  const Resolved = VERSION_COMPONENTS[version] || LandingPage;
  return (
    <div data-testid={`homepage-version-${version.toLowerCase()}`}>
      <Resolved />
    </div>
  );
};

export default HomepageVersionGate;
